/**
 * Pure (dependency-free) voice settings validation + migration helpers.
 *
 * Kept free of imports and non-erasable TypeScript syntax so the node
 * contract tests can load it directly with Node's type stripping.
 */

export type NoiseSuppresionState =
  | "disabled"
  | "browser"
  | "enhanced"
  | "studio";

export type VoiceGateMode = "auto" | "manual" | "off";

export type VoiceInputMode = "vad" | "ptt";

export const NOISE_SUPPRESSION_STATES: NoiseSuppresionState[] = [
  "disabled",
  "browser",
  "enhanced",
  "studio",
];

export const VOICE_GATE_MODES: VoiceGateMode[] = ["auto", "manual", "off"];

export const VOICE_INPUT_MODES: VoiceInputMode[] = ["vad", "ptt"];

/**
 * Bump when a one-time migration of stored processing settings is added.
 *
 * v1: users still on the old implicit default ("browser") move to RNNoise.
 */
export const VOICE_PROCESSING_VERSION = 1;

export const DEFAULT_NOISE_SUPPRESSION: NoiseSuppresionState = "enhanced";
export const DEFAULT_GATE_THRESHOLD_DB = -50;
export const GATE_THRESHOLD_MIN_DB = -90;
export const GATE_THRESHOLD_MAX_DB = -10;

export interface VoiceProcessingSettings {
  processingVersion: number;
  noiseSupression: NoiseSuppresionState;
  gateMode: VoiceGateMode;
  gateThresholdDb: number;
  inputMode: VoiceInputMode;
  pushToTalkKey?: string;
}

export function defaultVoiceProcessingSettings(): VoiceProcessingSettings {
  return {
    processingVersion: VOICE_PROCESSING_VERSION,
    noiseSupression: DEFAULT_NOISE_SUPPRESSION,
    gateMode: "auto",
    gateThresholdDb: DEFAULT_GATE_THRESHOLD_DB,
    inputMode: "vad",
  };
}

/**
 * Validate and migrate stored processing settings.
 *
 * An explicitly stored "browser" is indistinguishable from the historic
 * default, so every store written before `processingVersion` existed is
 * treated as "never chose" and moved to RNNoise exactly once. Stores written
 * after the migration carry `processingVersion`, so a deliberate later choice
 * of "browser" is respected.
 */
export function cleanVoiceProcessingSettings(
  input: Record<string, unknown>,
): VoiceProcessingSettings {
  const data = defaultVoiceProcessingSettings();

  const storedVersion =
    typeof input.processingVersion === "number" &&
    Number.isFinite(input.processingVersion)
      ? input.processingVersion
      : 0;

  // legacy boolean-ish values from very old clients
  let mode: NoiseSuppresionState | undefined;
  if (input.noiseSupression === "true" || input.noiseSupression === true) {
    mode = "browser";
  } else if (
    input.noiseSupression === "false" ||
    input.noiseSupression === false
  ) {
    mode = "disabled";
  } else if (
    typeof input.noiseSupression === "string" &&
    (NOISE_SUPPRESSION_STATES as string[]).includes(input.noiseSupression)
  ) {
    mode = input.noiseSupression as NoiseSuppresionState;
  }

  if (mode) {
    data.noiseSupression =
      storedVersion < 1 && mode === "browser" ? "enhanced" : mode;
  }

  if (
    typeof input.gateMode === "string" &&
    (VOICE_GATE_MODES as string[]).includes(input.gateMode)
  ) {
    data.gateMode = input.gateMode as VoiceGateMode;
  }

  if (
    typeof input.gateThresholdDb === "number" &&
    Number.isFinite(input.gateThresholdDb)
  ) {
    data.gateThresholdDb = clampThresholdDb(input.gateThresholdDb);
  }

  if (
    typeof input.inputMode === "string" &&
    (VOICE_INPUT_MODES as string[]).includes(input.inputMode)
  ) {
    data.inputMode = input.inputMode as VoiceInputMode;
  }

  if (
    typeof input.pushToTalkKey === "string" &&
    input.pushToTalkKey.length > 0 &&
    input.pushToTalkKey.length <= 64
  ) {
    data.pushToTalkKey = input.pushToTalkKey;
  }

  data.processingVersion = Math.max(storedVersion, VOICE_PROCESSING_VERSION);
  return data;
}

export function clampThresholdDb(value: number) {
  return Math.max(
    GATE_THRESHOLD_MIN_DB,
    Math.min(GATE_THRESHOLD_MAX_DB, Math.round(value)),
  );
}

/**
 * Push-to-talk key handling policy.
 *
 * The bound key always works while the Mutiny window is focused, EXCEPT when
 * it is a printable character and focus is inside an editable field — then
 * the keystroke belongs to the text being typed. Non-printable keys (F-keys,
 * modifiers, CapsLock, Insert, mouse-side keys mapped to keys, …) work
 * everywhere, including while typing.
 */
export function shouldHandlePushToTalkKey(event: {
  code: string;
  key: string;
  boundCode: string | undefined;
  targetIsEditable: boolean;
}) {
  if (!event.boundCode || event.code !== event.boundCode) return false;
  const printable = event.key.length === 1;
  return !(printable && event.targetIsEditable);
}
